import { ROUTES } from "@/constants/routes";
import { route } from "@/utils/route";
import type {
  BillingInterval,
  UpdateSubscriptionPayload,
} from "@cohesive/api-client";
import type { SetupIntent } from "@stripe/stripe-js";

/**
 * What a card is being collected for — the values the checkout URL carries.
 * Nullable because the page path parses them out of a query string; the dialog
 * narrows them (see `PaymentMethodDialogSession`).
 */
export type PaymentMethodIntent = {
  planName: string | null;
  billingCycle: BillingInterval | null;
  userCount: number;
  isPaymentMethodUpdate: boolean;
  keepTrial: boolean;
};

/** Stripe hands the method back expanded or as a bare id. */
export function paymentMethodIdFrom(
  setupIntent: SetupIntent | null | undefined,
): string | null {
  const method = setupIntent?.payment_method;
  if (typeof method === "string") return method;
  if (method && typeof method === "object" && method.id) return method.id;
  return null;
}

/** The query both the checkout URL and the confirm-setup return URL carry. */
export function paymentMethodSetupQuery(
  intent: PaymentMethodIntent,
  redirectTo: string | null,
): Record<string, string> {
  return {
    ...(intent.isPaymentMethodUpdate ? { mode: "payment_method_update" } : {}),
    ...(intent.planName && intent.billingCycle
      ? {
          plan: intent.planName,
          billing_cycle: intent.billingCycle,
          user_count: String(intent.userCount),
        }
      : {}),
    ...(intent.keepTrial ? { keep_trial: "1" } : {}),
    ...(redirectTo ? { redirect_to: redirectTo } : {}),
  };
}

/** Where Stripe returns after a redirect-based confirmation. */
export function confirmSetupReturnUrl(params: {
  intent: PaymentMethodIntent;
  redirectTo: string | null;
  /** Onboarding round-trips land on their own confirm route. */
  confirmSetupRoute?: string;
}): string {
  const {
    intent,
    redirectTo,
    confirmSetupRoute = ROUTES.BILLING.CONFIRM_SETUP,
  } = params;
  return (
    window.location.origin +
    route(confirmSetupRoute, {}, paymentMethodSetupQuery(intent, redirectTo))
  );
}

/**
 * The subscription write that makes a confirmed card the default. Null when the
 * caller carried no plan triple: the SetupIntent has already attached the
 * method to the customer, so there is nothing to send — but nothing makes it
 * the default either, which is why the caller must still refetch.
 */
export function buildPaymentMethodUpdate(params: {
  intent: PaymentMethodIntent;
  paymentMethodId: string;
  hasExistingSubscription: boolean;
  existingSubscriptionStatus: string | undefined;
}): UpdateSubscriptionPayload | null {
  const { intent, paymentMethodId, hasExistingSubscription } = params;
  if (!intent.planName || !intent.billingCycle || !intent.userCount)
    return null;

  return {
    plan_name: intent.planName,
    billing_interval: intent.billingCycle,
    seat_count: intent.userCount,
    default_payment_method_id: paymentMethodId,
    // Only end a trial that already exists — a new subscription gets its trial
    // from the backend, and a keep_trial attach spends none of it.
    trial_end_now:
      params.existingSubscriptionStatus === "trialing" &&
      hasExistingSubscription &&
      !intent.keepTrial,
  };
}
